import {
  DarkModeOutlined,
  LightModeOutlined,
  LogoutOutlined,
  NewReleases,
  SettingsOutlined,
  SupportOutlined,
} from "@mui/icons-material";
import AddCircleOutlineSharpIcon from "@mui/icons-material/AddCircleOutlineSharp";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  Drawer,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import muzukuruLogo from "../../../assets/muzukuru2.png";
import {
  AppItem,
  apps as defaultApps,
  useCustomApps,
} from "../../../layout/components/app-launcher/App";
import AppLauncher, {
  CUSTOM_ICON_MAP,
} from "../../../layout/components/app-launcher/AppLauncher";
import ToolsAssetsDrawer from "../../../layout/ToolsAssetsDrawer";
import { useAppDispatch } from "../../../store/hooks";
import { RootState } from "../../../store/Index";
import { logout, User } from "../../../store/slice/AuthSlice";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";
import { ColorModeContext, tokens } from "../../../theme";
import { AppModuleTabs } from "./AppModulesTabs";
import { AppsIcon } from "./icons/AppsIcon";
import { BrushIcon } from "./icons/BrushIcon";
import { UserIcon } from "./icons/UserIcon";
import NotificationBell from "../../../layout/NotificationBell";
import CommentsIcon from "../../../layout/CommentsIcon";

const NavBar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const gridIconRef = useRef<HTMLButtonElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [openPopper, setOpenPopper] = useState(false);
  const [openAppsGrid, setOpenAppsGrid] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);


  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));

  // const user = useSelector(
  //   (state: RootState) => state.auth.user,
  // ) as User | null;
  // const dispatch = useDispatch<AppDispatch>();
  const dispatch = useAppDispatch();
  const user = useSelector(
    (state: RootState) => state.auth.user,
  ) as User | null;
  const navigate = useNavigate();
  const closePopperTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- Popper handlers ---
  const clearTimeoutRef = () => {
    if (closePopperTimeoutRef.current !== null) {
      clearTimeout(closePopperTimeoutRef.current);
      closePopperTimeoutRef.current = null;
    }
  };

  const handleOpenPopper = () => {
    clearTimeoutRef();
    setAnchorEl(avatarRef.current);
    setOpenPopper(true);
  };

  const handleClosePopper = () => {
    clearTimeoutRef();
    closePopperTimeoutRef.current = setTimeout(() => {
      setOpenPopper(false);
    }, 200);
  };

  // Apps Grid handlers
  const handleToggleAppsGrid = () => {
    setOpenAppsGrid((prev) => !prev);
  };

  const handleCloseAppsGrid = () => {
    setOpenAppsGrid(false);
  };

  const handleOpenMobileMenu = () => setOpenMobileMenu(true);
  const handleCloseMobileMenu = () => setOpenMobileMenu(false);

  // --- Profile menu actions ---
  const handleWhatsNew = () => {
    setOpenPopper(false);
    navigate("/changelog");
  };
  const handleLogout = () => {
    dispatch(logout());
    setOpenPopper(false);
    navigate("/login");
  };
  const handleSettings = () => {
    navigate("/settings");
    setOpenPopper(false);
  };
  const handleSupport = () => {
    window.open("https://dashboard.tawk.to/#/dashboard/69fda3775c9d2e1c33d14761", "_blank");
  };

  const [openToolsDrawer, setOpenToolsDrawer] = useState(false);
  const closeAppsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [appOrder, setAppOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem("appOrder");
    if (saved) {
      try {
        return JSON.parse(saved) as string[];
      } catch {
        return [];
      }
    }
    return [];
  });

  const { customApps, addCustomApp, removeCustomApp } = useCustomApps();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canAddBooking = hasPermission(PERMISSIONS.BOOKING_ADD);
  const canSeeNotifications = hasPermission(PERMISSIONS.NOTIFICATION_VIEW);
  const canSeeComments = hasPermission(PERMISSIONS.COMMENT_VIEW);

  const canSeeApp = (app: AppItem) => {
    if (!app.permission) return true;
    return Array.isArray(app.permission)
      ? hasAnyPermission(app.permission)
      : hasPermission(app.permission);
  };

  const apps = useMemo(() => {
    const customAsAppItems: AppItem[] = customApps.map((stored) => ({
      label: stored.label,
      route: stored.route,
      color: stored.color,
      icon: CUSTOM_ICON_MAP[stored.iconKey] ?? CUSTOM_ICON_MAP["OpenInNew"],
      isCustom: true,
    }));

    const allApps = [...defaultApps, ...customAsAppItems].filter(canSeeApp);

    if (appOrder.length === 0) return allApps;

    const reordered = appOrder
      .map((label) => allApps.find((app) => app.label === label))
      .filter((app): app is AppItem => app !== undefined);

    const reorderedLabels = new Set(appOrder);
    const unseen = allApps.filter((app) => !reorderedLabels.has(app.label));

    return [...reordered, ...unseen];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appOrder, customApps, hasPermission, hasAnyPermission]);

  const handleReorder = (newOrder: AppItem[]) => {
    const labels = newOrder.map((app) => app.label);
    setAppOrder(labels);
    localStorage.setItem("appOrder", JSON.stringify(labels));
  };

  const handleRemoveCustomApp = (label: string) => {
    removeCustomApp(label);
    setAppOrder((prev) => {
      const next = prev.filter((l) => l !== label);
      localStorage.setItem("appOrder", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== "appOrder") return;

      try {
        setAppOrder(event.newValue ? JSON.parse(event.newValue) : []);
      } catch (error) {
        console.error("Failed to sync app order from localStorage", error);
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const handleOpenTools = () => setOpenToolsDrawer(true);
  const handleCloseTools = () => setOpenToolsDrawer(false);

  const clearAppsTimeout = () => {
    if (closeAppsTimeoutRef.current) {
      clearTimeout(closeAppsTimeoutRef.current);
      closeAppsTimeoutRef.current = null;
    }
  };

  const scheduleCloseApps = () => {
    clearAppsTimeout();
    closeAppsTimeoutRef.current = setTimeout(() => {
      setOpenAppsGrid(false);
    }, 800);
  };

  if (!user) return null;

  const avatarInitial = user.username?.charAt(0)?.toUpperCase() || "?";

  const flatIconButton = {
    borderRadius: "50%",
    border: `1px solid ${colors.primary[300]}`,
    backgroundColor: "transparent",
    color: colors.primary[700],
    width: 40,
    height: 40,

    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  };

  return (
    <Box
      className="topbar"
      width="100%"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        backgroundColor: `${colors.primary[900]}cc`,
        backdropFilter: "blur(6px)",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        //height={68}
        px={{ xs: 2, md: 4, lg: 8, xl: 20 }}
        sx={{
          backgroundColor: "transparent",
          pt: 4,
          pb: 2,
        }}
      >
        {/* Left: Logo/Brand */}
        <Box display="flex" alignItems="center">
          {isMobile ? (
            <IconButton
              onClick={handleOpenMobileMenu}
              sx={{ p: 0.5 }}
              aria-label="menu"
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box
              component="img"
              src={muzukuruLogo}
              alt="Muzukuru Logo"
              sx={{
                height: { md: 56, lg: 66 },
                width: "auto",
                cursor: "pointer",
                mr: { md: 2, lg: 4 },
              }}
              // onClick={() => navigate("/")}
            />
          )}
        </Box>

        {!isMobile && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AppModuleTabs variant="navbar" maxVisibleItems={isTablet ? 2 : 4} />
          </Box>
        )}

        {/* Right: Action Icons */}
        <Box
          display="flex"
          alignItems="center"
          sx={{
            gap: { xs: 1, md: 1.5, lg: 2 },
          }}
        >
          {!isMobile && (
            <>
              <ClickAwayListener onClickAway={handleCloseAppsGrid}>
                <Box>
                  <Tooltip title="Apps" arrow>
                    <IconButton
                      ref={gridIconRef}
                      aria-label="Apps"
                      onClick={handleToggleAppsGrid}
                      sx={flatIconButton}
                    >
                      <AppsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* <AppsGridPopover
                    open={openAppsGrid}
                    anchorEl={gridIconRef.current}
                    apps={appModules}
                    onClose={handleCloseAppsGrid}
                  /> */}
                  <AppLauncher
                    anchorEl={gridIconRef.current}
                    open={openAppsGrid}
                    onClose={handleCloseAppsGrid}
                    onMouseEnter={clearAppsTimeout}
                    onMouseLeave={scheduleCloseApps}
                    onOpenToolsDrawer={handleOpenTools}
                    appList={apps}
                    placement="bottom-start"
                    offset={[-20, 8]}
                    onReorder={handleReorder}
                    onAddCustomApp={(app) => addCustomApp(app)}   
                    onRemoveCustomApp={handleRemoveCustomApp} 
                  />
                </Box>
              </ClickAwayListener>
              {canAddBooking && (
                <Tooltip title="Add Booking" arrow>
                  <IconButton
                    aria-label="Add booking"
                    onClick={() => navigate("/bookings/add/funeral")}
                    sx={flatIconButton}
                  >
                    <BrushIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Toggle Theme" arrow>
                <IconButton
                  aria-label="Toggle theme"
                  onClick={colorMode.toggleColorMode}
                  sx={flatIconButton}
                >
                  {theme.palette.mode === "dark" ? (
                    <LightModeOutlined fontSize="small" />
                  ) : (
                    <DarkModeOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              {canSeeNotifications && <NotificationBell iconButtonSx={flatIconButton} />}
              {canSeeComments && <CommentsIcon iconButtonSx={flatIconButton} />}
            </>
          )}

          {/* --- Profile Avatar with Notification Badge --- */}
          <Box
            ref={avatarRef}
            onMouseEnter={!isMobile ? handleOpenPopper : undefined}
            onMouseLeave={!isMobile ? handleClosePopper : undefined}
            onClick={isMobile ? handleOpenPopper : undefined}
            onKeyDown={(e) => e.key === "Enter" && handleOpenPopper()}
            tabIndex={0}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              p: 1,
              position: "relative",
            }}
            role="button"
            aria-label="User profile"
          >
            <Avatar
              src={user?.image_url || undefined}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: colors.primary[700],
              }}
            >
              {!user?.image_url && (
                <UserIcon sx={{ color: "white" }} />
              )}
            </Avatar>
          </Box>

          <Popper
            open={openPopper}
            anchorEl={anchorEl}
            placement="bottom-end"
            sx={{ zIndex: 11 }}
            modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
          >
            <Paper
              onMouseEnter={!isMobile ? handleOpenPopper : undefined}
              onMouseLeave={!isMobile ? handleClosePopper : undefined}
              elevation={3}
              sx={{ width: 250, borderRadius: theme.shape.borderRadius }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Avatar
                  src={user?.image_url || undefined}
                  sx={{ width: 48, height: 48 }}
                >
                  {avatarInitial}
                </Avatar>
                <Typography variant="subtitle1" fontWeight="bold">
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.username}
                </Typography>
              </Box>
              <Divider />
              <ListItemButton onClick={handleWhatsNew} aria-label="What's New">
                <ListItemIcon>
                  <NewReleases />
                </ListItemIcon>
                <ListItemText primary="What's New" />
              </ListItemButton>
              <ListItemButton onClick={handleSettings} aria-label="Settings">
                <ListItemIcon>
                  <SettingsOutlined />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>
              <ListItemButton onClick={handleSupport} aria-label="Support">
                <ListItemIcon>
                  <SupportOutlined />
                </ListItemIcon>
                <ListItemText primary="Support" />
              </ListItemButton>
              <Divider />
              <ListItemButton onClick={handleLogout} aria-label="Logout">
                <ListItemIcon>
                  <LogoutOutlined />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </Paper>
          </Popper>
        </Box>
      </Box>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={openMobileMenu}
        onClose={handleCloseMobileMenu}
      >
        <Box
          sx={{
            width: 280,
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            backgroundColor: colors.primary[900],
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" fontWeight="bold">
              MUZUKURU
            </Typography>
            <IconButton
              onClick={handleCloseMobileMenu}
              aria-label="close menu"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <Avatar sx={{ width: 40, height: 40 }} src={user?.image_url || undefined}>
              {avatarInitial}
            </Avatar>
            <Typography variant="subtitle2" fontWeight="bold">
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : user?.username || "Guest"}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {canAddBooking && (
              <ListItemButton
                onClick={() => {
                  navigate("bookings/add/funeral");
                  handleCloseMobileMenu();
                }}
              >
                <ListItemIcon>
                  <AddCircleOutlineSharpIcon />
                </ListItemIcon>
                <ListItemText primary="Add Booking" />
              </ListItemButton>
            )}

            {/* Apps Section in Mobile */}
            <Typography
              variant="caption"
              sx={{ px: 2, pt: 2, pb: 1, opacity: 0.7, fontWeight: 600 }}
            >
              APPS & TOOLS
            </Typography>
            {apps.map((app) => {
              const IconComponent = app.icon;
              return (
                <ListItemButton
                  key={app.label}
                  onClick={() => {
                    if (app.route) {
                      navigate(app.route);
                    }
                    handleCloseMobileMenu();
                  }}
                >
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: app.color,
                      }}
                    >
                      <IconComponent sx={{ fontSize: 18, color: "white" }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText primary={app.label} />
                </ListItemButton>
              );
            })}

            <Divider sx={{ my: 2 }} />

            <ListItemButton onClick={colorMode.toggleColorMode}>
              <ListItemIcon>
                {theme.palette.mode === "dark" ? (
                  <LightModeOutlined />
                ) : (
                  <DarkModeOutlined />
                )}
              </ListItemIcon>
              <ListItemText primary="Toggle Theme" />
            </ListItemButton>

            <ListItemButton
              onClick={() => {
                navigate("/changelog");
                handleCloseMobileMenu();
              }}
            >
              <ListItemIcon>
                <NewReleases />
              </ListItemIcon>
              <ListItemText primary="What's New" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                navigate("/settings");
                handleCloseMobileMenu();
              }}
            >
              <ListItemIcon>
                <SettingsOutlined />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
            <ListItemButton onClick={handleSupport}>
              <ListItemIcon>
                <SupportOutlined />
              </ListItemIcon>
              <ListItemText primary="Support" />
            </ListItemButton>
            <Divider sx={{ my: 1 }} />
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutOutlined />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </Box>
        </Box>
      </Drawer>
      <ToolsAssetsDrawer open={openToolsDrawer} onClose={handleCloseTools} />
    </Box>
  );
};

export default NavBar;
